/**
 * UranIA PA — Admin tenant list (US-028 AC#1+AC#3+AC#5+AC#6).
 *
 * Route `/admin/tenants` — pagina list-only di amministrazione tenant
 * (analisi §10 BC Identity & Access + §11 modello dati tenants + §18
 * sicurezza).
 *
 * AC coperti dal componente:
 *   - AC#1 — TanStack Query `useQuery(['admin', 'tenants'])` su
 *     `listTenants()` (api wrapper in `lib/admin/tenants-api.ts`).
 *   - AC#3 — RBAC guard: solo `platform_admin` (claim realm role).
 *     Niente fetch se l'utente non ha il ruolo; mostra alert.
 *   - AC#5 — Markup Bootstrap Italia 2.x semantico (table caption,
 *     scope=col, role status), zero violazioni axe-core serious
 *     (verificato da `spa-admin-tenants-axe.spec.ts`).
 *   - AC#6 — Typecheck (tsc -b strict) verde.
 *
 * Boundary:
 *   - L'autorizzazione reale è server-side (step-up MFA RFC 9470 + ruolo
 *     `platform_admin` su `GET /api/v1/tenants`). La visibilità lato SPA
 *     è UX: un utente senza ruolo arriva in 403 al click "Disabilita"
 *     anche se forza il path.
 *   - Lo stato `disabled` è già modellato dall'API (`status` enum
 *     `active|disabled` + `disabled_at` ISO 8601 ad audit).
 *   - Il bottone "Disabilita" / "Riabilita" naviga in detail (`$tenantId`)
 *     dove l'azione è effettiva con conferma esplicita + reason free-text
 *     (US-028 AC#4 step-up MFA).
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  createTenant,
  listTenants,
  TenantsApiError,
} from '../../../lib/admin/tenants-api.ts';
import type {
  CreateTenantBody,
  CreateTenantResponseDto,
  TenantDataClassification,
  TenantListDto,
  TenantSummaryDto,
} from '../../../lib/admin/tenants-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { Route as RootRoute } from '../../__root.tsx';

const ADMIN_TENANTS_QUERY_KEY = ['admin', 'tenants'] as const;

function TenantStatusBadge(props: { readonly status: TenantSummaryDto['status'] }): JSX.Element {
  const { t } = useTranslation();
  const variant = props.status === 'active' ? 'bg-success' : 'bg-secondary';
  return (
    <span className={`badge ${variant}`} data-testid="admin-tenant-status-badge">
      {t(`admin.tenants.status.${props.status}`)}
    </span>
  );
}

const CODICE_ADG_PATTERN = /^[A-Z0-9-]{2,32}$/u;
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/u;

interface CreateTenantFormProps {
  readonly onSuccess: (created: CreateTenantResponseDto) => void;
  readonly onClose: () => void;
}

function CreateTenantForm(props: CreateTenantFormProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nomeId = useId();
  const codiceId = useId();
  const regioneId = useId();
  const dpoId = useId();
  const classId = useId();

  const [nome, setNome] = useState('');
  const [codiceAdg, setCodiceAdg] = useState('');
  const [regione, setRegione] = useState('');
  const [dpoEmail, setDpoEmail] = useState('');
  const [classification, setClassification] =
    useState<TenantDataClassification>('internal');
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation<CreateTenantResponseDto, TenantsApiError, CreateTenantBody>({
    mutationFn: createTenant,
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      props.onSuccess(created);
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setValidationError(null);
    if (!CODICE_ADG_PATTERN.test(codiceAdg)) {
      setValidationError(t('admin.tenants.create.errors.codice_adg_invalid'));
      return;
    }
    if (!EMAIL_PATTERN.test(dpoEmail)) {
      setValidationError(t('admin.tenants.create.errors.dpo_email_invalid'));
      return;
    }
    if (nome.trim().length === 0 || regione.trim().length === 0) {
      setValidationError(t('admin.tenants.create.errors.required'));
      return;
    }
    mutation.mutate({
      nome: nome.trim(),
      codice_adg: codiceAdg.trim(),
      regione: regione.trim(),
      dpo_email: dpoEmail.trim(),
      data_classification_default: classification,
    });
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-tenants-create-form">
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor={nomeId} className="form-label small text-muted mb-1">
            {t('admin.tenants.create.fields.nome')}
          </label>
          <input
            id={nomeId}
            type="text"
            className="form-control form-control-sm"
            value={nome}
            onChange={(e): void => setNome(e.target.value)}
            required
            maxLength={255}
            data-testid="admin-tenants-create-nome"
          />
        </div>
        <div className="col-md-3">
          <label htmlFor={codiceId} className="form-label small text-muted mb-1">
            {t('admin.tenants.create.fields.codice_adg')}
          </label>
          <input
            id={codiceId}
            type="text"
            className="form-control form-control-sm font-monospace text-uppercase"
            value={codiceAdg}
            onChange={(e): void => setCodiceAdg(e.target.value.toUpperCase())}
            required
            pattern={CODICE_ADG_PATTERN.source}
            placeholder="ES. SIC, PR-FESR-LAZIO-2127"
            data-testid="admin-tenants-create-codice"
          />
          <small className="form-text text-muted">
            {t('admin.tenants.create.fields.codice_adg_hint')}
          </small>
        </div>
        <div className="col-md-3">
          <label htmlFor={regioneId} className="form-label small text-muted mb-1">
            {t('admin.tenants.create.fields.regione')}
          </label>
          <input
            id={regioneId}
            type="text"
            className="form-control form-control-sm"
            value={regione}
            onChange={(e): void => setRegione(e.target.value)}
            required
            maxLength={120}
            data-testid="admin-tenants-create-regione"
          />
        </div>
        <div className="col-md-8">
          <label htmlFor={dpoId} className="form-label small text-muted mb-1">
            {t('admin.tenants.create.fields.dpo_email')}
          </label>
          <input
            id={dpoId}
            type="email"
            className="form-control form-control-sm"
            value={dpoEmail}
            onChange={(e): void => setDpoEmail(e.target.value)}
            required
            data-testid="admin-tenants-create-dpo"
          />
        </div>
        <div className="col-md-4">
          <label htmlFor={classId} className="form-label small text-muted mb-1">
            {t('admin.tenants.create.fields.data_classification_default')}
          </label>
          <select
            id={classId}
            className="form-select form-select-sm"
            value={classification}
            onChange={(e): void =>
              setClassification(e.target.value as TenantDataClassification)
            }
          >
            <option value="public">public</option>
            <option value="internal">internal</option>
            <option value="restricted">restricted</option>
          </select>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
        <div className="flex-grow-1">
          {validationError !== null && (
            <div className="text-danger small" role="alert">
              {validationError}
            </div>
          )}
          {mutation.isError && (
            <div className="text-danger small" role="alert">
              {mutation.error.detail ?? mutation.error.message}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={props.onClose}
        >
          {t('admin.tenants.create.close')}
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={mutation.isPending}
          data-testid="admin-tenants-create-submit"
        >
          {mutation.isPending
            ? t('admin.tenants.create.submitting')
            : t('admin.tenants.create.submit')}
        </button>
      </div>
    </form>
  );
}

function TenantTable(props: { readonly tenants: readonly TenantSummaryDto[] }): JSX.Element {
  const { t } = useTranslation();
  if (props.tenants.length === 0) {
    return (
      <p
        className="text-muted"
        role="status"
        aria-live="polite"
        data-testid="admin-tenants-empty"
      >
        {t('admin.tenants.empty')}
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <table className="table table-striped" data-testid="admin-tenants-table">
        <caption className="visually-hidden">{t('admin.tenants.table_caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.tenants.columns.codice_adg')}</th>
            <th scope="col">{t('admin.tenants.columns.nome')}</th>
            <th scope="col">{t('admin.tenants.columns.regione')}</th>
            <th scope="col">{t('admin.tenants.columns.status')}</th>
            <th scope="col" className="text-end">
              <span className="visually-hidden">{t('admin.tenants.columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.tenants.map((tenant) => (
            <tr key={tenant.tenant_id} data-testid={`admin-tenants-row-${tenant.codice_adg}`}>
              <th scope="row">
                <code>{tenant.codice_adg}</code>
              </th>
              <td>{tenant.nome}</td>
              <td>{tenant.regione}</td>
              <td>
                <TenantStatusBadge status={tenant.status} />
              </td>
              <td className="text-end">
                <Link
                  to="/admin/tenants/$tenantId"
                  params={{ tenantId: tenant.tenant_id }}
                  className="btn btn-sm btn-outline-primary"
                  data-testid={`admin-tenants-detail-${tenant.codice_adg}`}
                >
                  {t('admin.tenants.actions.open_detail')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminTenantListPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);
  const [showCreate, setShowCreate] = useState(false);

  const query = useQuery<TenantListDto, TenantsApiError>({
    queryKey: ADMIN_TENANTS_QUERY_KEY,
    queryFn: listTenants,
    enabled: isAuthenticated && isPlatformAdmin,
    staleTime: 30_000,
  });

  if (!isAuthenticated) {
    return (
      <section className="urania-admin-tenants" data-testid="admin-tenants-unauthenticated">
        <h1 className="display-5 mb-3">{t('admin.tenants.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('admin.tenants.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('admin.tenants.unauthenticated.message')}</p>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="admin-tenants-go-to-login"
          >
            {t('admin.tenants.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section className="urania-admin-tenants" data-testid="admin-tenants-forbidden">
        <h1 className="display-5 mb-3">{t('admin.tenants.title')}</h1>
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="admin-tenants-forbidden-alert"
        >
          <strong>{t('admin.tenants.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.tenants.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-tenants" data-testid="admin-tenants-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>
      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div
          className="position-relative d-flex justify-content-between align-items-start gap-3 flex-wrap"
          style={{ zIndex: 1 }}
        >
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            >
              ADMIN · TENANT
            </span>
            <h1 className="h3 mb-2">{t('admin.tenants.title')}</h1>
            <p className="mb-0 opacity-90 small">{t('admin.tenants.subtitle')}</p>
          </div>
          <button
            type="button"
            className="btn btn-light"
            onClick={(): void => setShowCreate((v) => !v)}
            data-testid="admin-tenants-new"
          >
            + {t('admin.tenants.create.cta')}
          </button>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.12 }}
          width="200"
          height="200"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-2.5v-2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5V15h-2.5a.5.5 0 0 1-.5-.5v-12z" />
          <path d="M3 14.5V14a.5.5 0 0 1 .5-.5H4V2.5A1.5 1.5 0 0 1 5.5 1h5A1.5 1.5 0 0 1 12 2.5V13.5h.5a.5.5 0 0 1 .5.5v.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5z" />
        </svg>
      </div>

      {showCreate && (
        <div className="card shadow-sm border-0 mb-3" data-testid="admin-tenants-create-card">
          <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {t('admin.tenants.create.heading')}
            </span>
          </div>
          <div className="card-body">
            <CreateTenantForm
              onSuccess={(): void => setShowCreate(false)}
              onClose={(): void => setShowCreate(false)}
            />
          </div>
        </div>
      )}

      {query.isLoading && (
        <p role="status" aria-live="polite" data-testid="admin-tenants-loading">
          {t('admin.tenants.loading')}
        </p>
      )}

      {query.isError && (
        <div className="alert alert-danger" role="alert" data-testid="admin-tenants-error">
          <strong>{t('admin.tenants.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {query.isSuccess && <TenantTable tenants={query.data.items} />}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/tenants',
  component: AdminTenantListPage,
});

export { ADMIN_TENANTS_QUERY_KEY };
